import { ValueDisplay } from 'ui/common';
import * as React from 'react';
import { SingleKeyValueComponent } from 'ui/common';
import { compose } from 'redux';
import styles from './display-text.scss';
import { Label, NoValueDash } from 'ui/common';
import { ReactNode } from 'react';

export interface DisplayTextProps extends SingleKeyValueComponent<string> {
  /**
   * Predefined possible styles by the design handbook
   */
  style?: 'primary' | 'secondary';
  /**
   * When true, the label font is normal and the value font is bold
   */
  strong?: boolean;
  /**
   * When true, the label font is normal and the value font is bold and uppercase
   */
  invertStrong?: boolean;
  /**
   * When true, the predefined style class for comment text style is applied
   */
  showAsComment?: boolean;
  /**
   * A prefix string to render in front of the text value
   */
  textPrefix?: string;
  /**
   * When true, a predefined style class to render the label above the value is applied
   */
  showStacked: boolean;
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;

  /**
   * Truncate value after 200 characters
   */
  shouldTruncate?: boolean;

  /**
   * Text on button to see the whole text
   */
  textDisplayMore?: string;

  /**
   * Text on button to collapse again
   */
  textDisplayLess?: string;

  /**
   * @internal
   */
  children?: ReactNode;
}

/**
 * Displaying a databound text value consist of rendering a label, and the value.
 *
 * This component is responsible for the composition of the two.
 *
 * @param props
 */
const DisplayTextComponent = (props: Partial<DisplayTextProps>) => {
  const {
    className,
    componentId,
    hideIfNoValue = false,
    label,
    valueItem,
    textPrefix,
    shouldTruncate,
    textDisplayMore,
    textDisplayLess
  } = props;
  
  const hideComponent = hideIfNoValue && !(valueItem && valueItem.value);
  const displayNoValueDash = !hideIfNoValue && !(valueItem && valueItem.value) && label && label.visible;  

  return (
    !hideComponent &&
      <div className={className} id={componentId}>
        <Label label={label} />
        <ValueDisplay
          valueItem={valueItem}
          prefix={textPrefix}
          {...{shouldTruncate, textDisplayMore, textDisplayLess}}
        />
        {displayNoValueDash && <NoValueDash/>}
      </div>
  );
};

/**
 * A utility that maps known props to a known style and appends them to a list.
 *
 * The list of final style classes are concatenated into a valid JSX className string attribute
 *
 * @param props
 */
const styleFromProps = (props: DisplayTextProps) =>
  [
    styles.text,
    props.className,
    styles[props.style],
    props.showStacked && styles.stackedColumn,
    props.showAsComment && styles.textComment,
    props.invertStrong && styles.invertStrong,
    props.strong && !props.invertStrong && styles.strong
  ].filter(s => s).join(' ');

/**
 * Higher order component to override the final className
 *
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => ({children, ...childProps}: DisplayTextProps) => (
  <Component {...childProps} className={styleFromProps(childProps)}>
    {children}
  </Component>
);

export const DisplayText = compose(
  styleVariations
)(DisplayTextComponent);
